import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Text, Loader } from '@pingux/astro';
import ArrowLeftIcon from '@pingux/mdi-react/ArrowLeftIcon';
import ContentCopyIcon from '@pingux/mdi-react/ContentCopyIcon';
import CheckIcon from '@pingux/mdi-react/CheckIcon';
import DeleteIcon from '@pingux/mdi-react/DeleteIcon';
import RefreshIcon from '@pingux/mdi-react/RefreshIcon';
import StatusBadge from '@components/StatusBadge';
import CredentialTotpSection from '@components/CredentialTotpSection/CredentialTotpSection';
import { credentialsService } from '@services/credentials';
import { requestsService } from '@services/requests';
import { Credential, SecretRequest } from '@app-types/tap-partner-vault';
import { useAuthContext } from 'state/context/AuthContext';
import { isGroupOwnersGroup, userIsGroupOwner } from 'utils/auth';

const REQUEST_STATUS_POLL_INTERVAL_MS = 3000;
const MAX_SHARE_EXPIRY_MINUTES = 90 * 24 * 60;
const SHARE_EXPIRY_UNIT_MULTIPLIERS = {
  minutes: 1,
  hours: 60,
  days: 24 * 60,
} as const;

type ShareExpiryUnit = keyof typeof SHARE_EXPIRY_UNIT_MULTIPLIERS;

const CredentialDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [credential, setCredential] = useState<Credential | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [isSecretRevealed, setIsSecretRevealed] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [secretLoading, setSecretLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareOptionsOpen, setShareOptionsOpen] = useState(false);
  const [shareExpiryAmount, setShareExpiryAmount] = useState('30');
  const [shareExpiryUnit, setShareExpiryUnit] = useState<ShareExpiryUnit>('minutes');
  const [shareExpiryError, setShareExpiryError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareAccessCode, setShareAccessCode] = useState<string | null>(null);
  const [shareExpiry, setShareExpiry] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [myRequest, setMyRequest] = useState<SecretRequest | null | undefined>(undefined);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const {
    state: { user },
  } = useAuthContext();
  const userRole = user?.role ?? 'user';
  const userId = user?.id ?? null;

  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
  const isSuperAdmin = userRole === 'superadmin';
  const isOwner = Boolean(userId && credential?.owner_id === userId);
  const userGroups = user?.groups ?? [];
  const isGroupOwner = userIsGroupOwner(userGroups);
  const canRevealAsGroupOwner = Boolean(
    isGroupOwner
      && credential
      && !credential.is_private
      && credential.group_tags.some((tag) => userGroups.includes(tag))
  );
  const canEdit = isAdmin || isOwner;
  const canDelete = isSuperAdmin ||
    (isAdmin && !credential?.is_private) ||
    (isOwner && Boolean(credential?.is_private)) ||
    (isGroupOwner && isOwner && !credential?.is_private);
  const canValidate = (isAdmin || (isGroupOwner && isOwner)) && !credential?.validation_disabled;
  const needsRequest = !isAdmin && !canRevealAsGroupOwner;
  const canRevealSecret = isAdmin || canRevealAsGroupOwner;
  const canShare = isAdmin || (isGroupOwner && isOwner);

  const fetchCredential = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await credentialsService.get(id);
      setCredential(data);
    } catch (err) {
      setError('Failed to load credential. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCredential();
  }, [fetchCredential]);

  // For regular users: fetch request status once, then poll only while pending.
  useEffect(() => {
    if (!id || !needsRequest) return;

    let cancelled = false;

    const loadRequestStatus = async () => {
      try {
        const req = await requestsService.getMyRequestStatus(id);
        if (!cancelled) {
          setMyRequest(current => {
            if (!req && current?.status === 'pending') {
              return current;
            }
            return req;
          });
        }
      } catch {
        if (!cancelled) {
          setMyRequest(current => current ?? null);
        }
      }
    };

    loadRequestStatus();

    return () => {
      cancelled = true;
    };
  }, [id, needsRequest]);

  useEffect(() => {
    if (!id || !needsRequest || myRequest?.status !== 'pending') return;

    let cancelled = false;
    let isPolling = false;

    const pollRequestStatus = async () => {
      if (isPolling) return;

      isPolling = true;
      try {
        const req = await requestsService.getMyRequestStatus(id);
        if (!cancelled) {
          setMyRequest(req);
        }
      } catch {
        // ignore transient polling errors
      } finally {
        isPolling = false;
      }
    };

    pollRequestStatus();
    const intervalId = setInterval(pollRequestStatus, REQUEST_STATUS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [id, needsRequest, myRequest?.status]);

  useEffect(() => {
    if (!id || !needsRequest || myRequest?.status !== 'approved' || secret !== null) return;

    let cancelled = false;

    const loadApprovedSecret = async () => {
      try {
        setSecretLoading(true);
        setError(null);
        const data = await credentialsService.getSecret(id);
        if (!cancelled) {
          setSecret(data.secret);
          setIsSecretRevealed(true);
        }
      } catch {
        if (!cancelled) {
          setError('Request approved, but failed to retrieve secret. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setSecretLoading(false);
        }
      }
    };

    loadApprovedSecret();

    return () => {
      cancelled = true;
    };
  }, [id, needsRequest, myRequest?.status, secret]);

  const handleCopy = useCallback(async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // clipboard unavailable
    }
  }, []);

  const handleRevealSecret = useCallback(async () => {
    if (!id) return;
    if (!secret) {
      try {
        setSecretLoading(true);
        const data = await credentialsService.getSecret(id);
        setSecret(data.secret);
      } catch (err) {
        setError('Failed to retrieve secret. Please try again.');
        return;
      } finally {
        setSecretLoading(false);
      }
    }
    setIsSecretRevealed(prev => !prev);
  }, [id, secret]);

  const handleValidate = useCallback(async () => {
    if (!id) return;

    try {
      setValidating(true);
      await credentialsService.validate(id);
      await fetchCredential();
    } catch (err) {
      setError('Failed to validate credential. Please try again.');
    } finally {
      setValidating(false);
    }
  }, [id, fetchCredential]);

  const getShareExpiryMinutes = useCallback(() => {
    const amount = Number(shareExpiryAmount);

    if (!Number.isInteger(amount) || amount < 1) {
      setShareExpiryError('Enter a whole number greater than 0.');
      return null;
    }

    const minutes = amount * SHARE_EXPIRY_UNIT_MULTIPLIERS[shareExpiryUnit];

    if (minutes > MAX_SHARE_EXPIRY_MINUTES) {
      setShareExpiryError('Share expiry can be up to 90 days.');
      return null;
    }

    setShareExpiryError(null);
    return minutes;
  }, [shareExpiryAmount, shareExpiryUnit]);

  const handleShareButton = useCallback(() => {
    setError(null);
    setShareExpiryError(null);
    setShareOptionsOpen((current) => {
      const next = !current;
      if (next) {
        setShareUrl(null);
        setShareAccessCode(null);
        setShareExpiry(null);
      }
      return next;
    });
  }, []);

  const handleShare = useCallback(async () => {
    if (!id) return;
    const expiresInMinutes = getShareExpiryMinutes();
    if (expiresInMinutes === null) return;

    try {
      setSharing(true);
      setError(null);
      const result = await credentialsService.share(id, {
        expires_in_minutes: expiresInMinutes,
      });
      const url = `${window.location.origin}/shared/${result.token}`;
      setShareUrl(url);
      setShareAccessCode(result.access_code);
      setShareExpiry(result.expires_at);
      setShareOptionsOpen(false);
      await navigator.clipboard.writeText(url).catch((_e) => undefined);
    } catch {
      setError('Failed to create share link. Please try again.');
    } finally {
      setSharing(false);
    }
  }, [id, getShareExpiryMinutes]);

  const handleRequest = useCallback(async () => {
    if (!id) return;
    try {
      setRequestSubmitting(true);
      const req = await requestsService.createRequest(id);
      setMyRequest(req);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to submit request';
      setError(msg);
    } finally {
      setRequestSubmitting(false);
    }
  }, [id]);

  const handleDelete = useCallback(async () => {
    if (!id) return;

    if (!window.confirm('Are you sure you want to delete this credential? This action cannot be undone.')) {
      return;
    }

    try {
      await credentialsService.delete(id);
      navigate('/');
    } catch (err) {
      setError('Failed to delete credential. Please try again.');
    }
  }, [id, navigate]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const detailCardStyle = {
    padding: '1.5rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    marginBottom: '1.5rem',
  };

  const detailGridStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    padding: '0.75rem 0',
  };

  const detailItemStyle: React.CSSProperties = {
    minWidth: '160px',
    textAlign: 'center',
  };

  const detailLabelStyle = {
    color: '#6b7280',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.25rem',
    textAlign: 'center' as const,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <Loader />
      </Box>
    );
  }

  if (!credential) {
    return (
      <Box sx={{ padding: '1.5rem 0' }}>
        <Box
          sx={{
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: '0.5rem',
          }}
        >
          <Text sx={{ color: '#6b7280' }}>Credential not found</Text>
          <Button variant="link" onPress={() => navigate('/')} sx={{ marginTop: '1rem' }}>
            Back to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '1.5rem 0' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'nowrap',
          }}
        >
          <Button
            variant="link"
            onPress={() => navigate('/')}
            sx={{ padding: '0.5rem' }}
          >
            <ArrowLeftIcon size={20} />
          </Button>
          {canValidate && (
            <Button
              variant="default"
              onPress={handleValidate}
              isLoading={validating}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}
            >
              <RefreshIcon size={18} />
              Validate
            </Button>
          )}
          {canEdit && (
            <Button
              variant="default"
              onPress={() => navigate(`/credentials/${credential.id}/edit`)}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}
            >
              Edit
            </Button>
          )}
          {canShare && (
            <Button
              variant="default"
              onPress={handleShareButton}
              isLoading={sharing}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}
            >
              Share
            </Button>
          )}
          {canDelete && (
            <Button
              variant="critical"
              onPress={handleDelete}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}
            >
              <DeleteIcon size={18} />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Box
          sx={{
            padding: '1rem',
            backgroundColor: '#fee2e2',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            borderLeft: '4px solid #dc2626',
          }}
        >
          <Text sx={{ color: '#991b1b' }}>{error}</Text>
        </Box>
      )}

      {/* Share options panel */}
      {shareOptionsOpen && (
        <Box sx={{
          padding: '1rem 1.25rem',
          backgroundColor: '#eff6ff',
          borderRadius: '0.5rem',
          borderLeft: '4px solid #2563eb',
          marginBottom: '1.5rem',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}>
            <div>
              <Text sx={{ fontSize: '0.7rem', fontWeight: '600', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                Share Expiry
              </Text>
              <input
                type="number"
                min={1}
                step={1}
                value={shareExpiryAmount}
                onChange={(event) => {
                  setShareExpiryAmount(event.target.value);
                  setShareExpiryError(null);
                }}
                style={{
                  width: '6rem',
                  height: '2.25rem',
                  border: `1px solid ${shareExpiryError ? '#dc2626' : '#bfdbfe'}`,
                  borderRadius: '0.375rem',
                  padding: '0 0.6rem',
                  color: '#111827',
                  backgroundColor: 'white',
                }}
              />
            </div>
            <div>
              <Text sx={{ fontSize: '0.7rem', fontWeight: '600', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                Unit
              </Text>
              <select
                value={shareExpiryUnit}
                onChange={(event) => {
                  setShareExpiryUnit(event.target.value as ShareExpiryUnit);
                  setShareExpiryError(null);
                }}
                style={{
                  height: '2.25rem',
                  border: '1px solid #bfdbfe',
                  borderRadius: '0.375rem',
                  padding: '0 0.6rem',
                  color: '#111827',
                  backgroundColor: 'white',
                }}
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
            <Button
              variant="primary"
              onPress={handleShare}
              isLoading={sharing}
              sx={{ height: '2.25rem', whiteSpace: 'nowrap' }}
            >
              Create link
            </Button>
            <Button
              variant="link"
              onPress={() => {
                setShareOptionsOpen(false);
                setShareExpiryError(null);
              }}
              sx={{ height: '2.25rem', whiteSpace: 'nowrap', color: '#6b7280' }}
            >
              Cancel
            </Button>
          </div>
          {shareExpiryError && (
            <Text sx={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.5rem' }}>
              {shareExpiryError}
            </Text>
          )}
        </Box>
      )}

      {/* Share link panel */}
      {shareUrl && (
        <Box sx={{
          padding: '1rem 1.25rem',
          backgroundColor: '#f0fdf4',
          borderRadius: '0.5rem',
          borderLeft: '4px solid #16a34a',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Text sx={{ fontSize: '0.8rem', fontWeight: '600', color: '#15803d', marginBottom: '0.25rem' }}>
                Share link created — copied to clipboard
              </Text>
              <Text sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#166534', wordBreak: 'break-all' }}>
                {shareUrl}
              </Text>
              <Text sx={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Expires {new Date(shareExpiry!).toLocaleString()}
              </Text>
            </Box>
            <Button
              variant="link"
              onPress={() => {
                navigator.clipboard.writeText(shareUrl).catch((_e) => undefined);
                setCopiedField('shareUrl');
                setTimeout(() => setCopiedField(null), 2000);
              }}
              sx={{ color: copiedField === 'shareUrl' ? '#059669' : '#6b7280', padding: '0', whiteSpace: 'nowrap', fontSize: '0.8rem' }}
            >
              {copiedField === 'shareUrl' ? '✓ Copied' : 'Copy link'}
            </Button>
            <Button
              variant="link"
              onPress={() => { setShareUrl(null); setShareAccessCode(null); setShareExpiry(null); }}
              sx={{ color: '#9ca3af', padding: '0', fontSize: '0.8rem' }}
            >
              Dismiss
            </Button>
          </div>
          {shareAccessCode && (
            <div style={{
              marginTop: '0.75rem',
              paddingTop: '0.75rem',
              borderTop: '1px solid #bbf7d0',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}>
              <div>
                <Text sx={{ fontSize: '0.7rem', fontWeight: '600', color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Access Code
                </Text>
                <Text sx={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: 'monospace', color: '#14532d', letterSpacing: '0.2em' }}>
                  {shareAccessCode}
                </Text>
              </div>
              <Text sx={{ fontSize: '0.75rem', color: '#166534', maxWidth: '22rem' }}>
                Share this code separately with the recipient. They will need it to view the credential.
              </Text>
            </div>
          )}
        </Box>
      )}

      {/* Credential Details */}
      <Box sx={detailCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
          <Text
            sx={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#111827',
            }}
          >
            {credential.partner} - {credential.tenant_id}
          </Text>
          {credential.is_private && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.7rem',
              fontWeight: '600',
              backgroundColor: '#ede9fe',
              color: '#6d28d9',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}>
              Private
            </span>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {credential.group_tags.filter((tag) => !isGroupOwnersGroup(tag)).map((tag) => (
            <span
              key={`${credential.id}-${tag}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.2rem 0.55rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '600',
                backgroundColor: '#dbeafe',
                color: '#1d4ed8',
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Primary Row: URL, Account, Secret */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '3rem',
          paddingBottom: '1rem',
          marginBottom: '1rem',
          borderBottom: '1px solid #e5e7eb',
        }}>
          {credential.url && (
            <div style={{ textAlign: 'center', maxWidth: '650px' }}>
              <Text sx={{ ...detailLabelStyle, fontSize: '0.8rem' }}>URL</Text>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <Text sx={{ fontWeight: '600', fontSize: '1rem', wordBreak: 'break-all', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{credential.url}</Text>
                <Button
                  variant="link"
                  onPress={() => handleCopy(credential.url!, 'url')}
                  sx={{ color: copiedField === 'url' ? '#059669' : '#6b7280', padding: '0' }}
                >
                  {copiedField === 'url' ? <CheckIcon size={15} /> : <ContentCopyIcon size={15} />}
                </Button>
              </div>
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <Text sx={{ ...detailLabelStyle, fontSize: '0.8rem' }}>Account</Text>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <Text sx={{ fontWeight: '600', fontSize: '1rem' }}>
                {credential.credential_type === 'api_key' ? 'API Key - No Account' : (credential.account_name ?? '—')}
              </Text>
              {credential.account_name && credential.credential_type !== 'api_key' && (
                <Button
                  variant="link"
                  onPress={() => handleCopy(credential.account_name!, 'account')}
                  sx={{ color: copiedField === 'account' ? '#059669' : '#6b7280', padding: '0' }}
                >
                  {copiedField === 'account' ? <CheckIcon size={15} /> : <ContentCopyIcon size={15} />}
                </Button>
              )}
            </div>
          </div>
          {canRevealSecret && (
            <div style={{ textAlign: 'center' }}>
              <Text sx={{ ...detailLabelStyle, fontSize: '0.8rem' }}>
                {credential.credential_type === 'oauth_client' ? 'Client Secret' :
                 credential.credential_type === 'admin_account' ? 'Password' :
                 credential.credential_type === 'basic_auth' ? 'Password' : 'API Key'}
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <Text sx={{
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  fontSize: '1rem',
                  color: isSecretRevealed ? '#111827' : '#9ca3af',
                  wordBreak: 'break-all',
                }}>
                  {isSecretRevealed && secret ? secret : '••••••••••••••••'}
                </Text>
                {isSecretRevealed && secret && (
                  <Button
                    variant="link"
                    onPress={() => handleCopy(secret, 'secret')}
                    sx={{ color: copiedField === 'secret' ? '#059669' : '#6b7280', padding: '0' }}
                  >
                    {copiedField === 'secret' ? <CheckIcon size={15} /> : <ContentCopyIcon size={15} />}
                  </Button>
                )}
                <Button
                  variant="link"
                  onPress={handleRevealSecret}
                  isDisabled={secretLoading}
                  sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                >
                  {secretLoading ? '...' : isSecretRevealed ? 'Hide' : 'Reveal'}
                </Button>
              </div>
            </div>
          )}
          {needsRequest && (
            <div style={{ textAlign: 'center' }}>
              <Text sx={{ ...detailLabelStyle, fontSize: '0.8rem' }}>
                {credential.credential_type === 'oauth_client' ? 'Client Secret' :
                 credential.credential_type === 'admin_account' ? 'Password' :
                 credential.credential_type === 'basic_auth' ? 'Password' : 'API Key'}
              </Text>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', marginTop: '0.25rem' }}>
                {/* No request yet */}
                {(myRequest === null || myRequest === undefined) && (
                  <Button
                    variant="default"
                    onPress={handleRequest}
                    isLoading={requestSubmitting}
                    sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    Request Access
                  </Button>
                )}
                {/* Pending */}
                {myRequest?.status === 'pending' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Loader size="sm" />
                    <Text sx={{ fontSize: '0.8rem', color: '#92400e' }}>Request pending…</Text>
                  </div>
                )}
                {/* Approved: load and auto-reveal the secret once polling sees the approval. */}
                {myRequest?.status === 'approved' && secretLoading && secret === null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Loader size="sm" />
                    <Text sx={{ fontSize: '0.8rem', color: '#065f46' }}>Loading secret...</Text>
                  </div>
                )}
                {myRequest?.status === 'approved' && isSecretRevealed && secret !== null && (
                  <>
                    <Text sx={{ fontFamily: 'monospace', fontWeight: '600', fontSize: '1rem', wordBreak: 'break-all' }}>
                      {secret}
                    </Text>
                    <Button
                      variant="link"
                      onPress={() => handleCopy(secret, 'secret')}
                      sx={{ color: copiedField === 'secret' ? '#059669' : '#6b7280', padding: '0', fontSize: '0.75rem' }}
                    >
                      {copiedField === 'secret' ? '✓ Copied' : 'Copy'}
                    </Button>
                  </>
                )}
                {/* Denied */}
                {myRequest?.status === 'denied' && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                    <Text sx={{ fontSize: '0.8rem', color: '#991b1b', fontWeight: '600' }}>Request denied</Text>
                    {myRequest.review_note && (
                      <Text sx={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                        &ldquo;{myRequest.review_note}&rdquo;
                      </Text>
                    )}
                    <Button
                      variant="link"
                      onPress={handleRequest}
                      isLoading={requestSubmitting}
                      sx={{ fontSize: '0.75rem', marginTop: '0.25rem' }}
                    >
                      Request Again
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Secondary Row: Partner, Tenant, Type, Status, Dates */}
        <div style={{ ...detailGridStyle, justifyContent: 'center' }}>
          <div style={detailItemStyle}>
            <Text sx={{ ...detailLabelStyle, fontSize: '0.7rem' }}>Credential Type</Text>
            <Text sx={{ fontWeight: '400', fontSize: '0.8rem', color: '#6b7280' }}>{credential.credential_type.replace('_', ' ')}</Text>
          </div>

          <div style={{ ...detailItemStyle, minWidth: '220px' }}>
            <Text sx={{ ...detailLabelStyle, fontSize: '0.7rem' }}>Group Tags</Text>
            <Text sx={{ fontWeight: '400', fontSize: '0.8rem', color: '#6b7280' }}>
              {(() => {
                const visibleTags = credential.group_tags.filter((tag) => !isGroupOwnersGroup(tag));
                return visibleTags.length > 0 ? visibleTags.join(', ') : '—';
              })()}
            </Text>
          </div>

          {(credential.credential_type === 'api_key' || credential.credential_type === 'basic_auth') && credential.curl_command && (
            <div style={{ ...detailItemStyle, maxWidth: '300px' }}>
              <Text sx={{ ...detailLabelStyle, fontSize: '0.7rem' }}>Curl Command</Text>
              <Text sx={{
                fontWeight: '400',
                fontSize: '0.75rem',
                color: '#6b7280',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}>
                {credential.curl_command}
              </Text>
            </div>
          )}

          <div style={detailItemStyle}>
            <Text sx={{ ...detailLabelStyle, fontSize: '0.7rem' }}>Validation Status</Text>
            <StatusBadge status={credential.validation_status} size="sm" />
          </div>

          <div style={detailItemStyle}>
            <Text sx={{ ...detailLabelStyle, fontSize: '0.7rem' }}>Last Validated</Text>
            <Text sx={{ fontWeight: '400', fontSize: '0.8rem', color: '#6b7280' }}>{formatDate(credential.last_validated_at)}</Text>
          </div>

          <div style={detailItemStyle}>
            <Text sx={{ ...detailLabelStyle, fontSize: '0.7rem' }}>Created</Text>
            <Text sx={{ fontWeight: '400', fontSize: '0.8rem', color: '#6b7280' }}>{formatDate(credential.created_at)}</Text>
          </div>
        </div>
      </Box>

      <CredentialTotpSection
        credential={credential}
        isAdminUser={isAdmin}
      />

      {/* Notes */}
      {credential.notes && (
        <Box sx={detailCardStyle}>
          <Text
            sx={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '0.75rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid #e5e7eb',
              textAlign: 'center',
            }}
          >
            Notes
          </Text>
          <Text sx={{ fontSize: '0.875rem', color: '#374151', whiteSpace: 'pre-wrap', textAlign: 'center' }}>
            {credential.notes}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default CredentialDetail;
